/**
 * iter645 — Extracted from MenuItemsPage.jsx (was lines ~1027-1089).
 * Foodics-style grid card for when the user prefers card layout over
 * the default list view. Pure presentational; all behaviour comes in
 * via props.
 */
import React from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { UtensilsCrossed, Upload, Star, Clock, Building2, Edit, Trash2, Globe } from 'lucide-react';
import { MenuMarginBadge } from '@/components/menu/MenuMarginBadge';

export function MenuGridCard({
  item,
  isSel,
  toggleSelect,
  handleEdit,
  handleDelete,
  handleImageUpload,
  getImageUrl,
  getBranchName,
  getPlatformName,
  getModifierCount,
  getModifierLabels,
  cur,
  fileInputRef,
  uploadItemRef,
  recipeCost = null,
}) {
  return (
    <div
      className={`bg-white dark:bg-muted rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-200 group ${isSel ? 'ring-2 ring-primary' : ''} ${!item.is_available ? 'opacity-60' : ''}`}
      data-testid={`menu-card-${item.id}`}
    >
      <div className="relative h-36 bg-gradient-to-br from-stone-50 to-stone-100 dark:from-stone-700 dark:to-stone-600 cursor-pointer" onClick={() => toggleSelect(item.id)}>
        {item.image_url ? (
          <img src={getImageUrl(item)} alt={item.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <UtensilsCrossed size={36} className="text-muted-foreground dark:text-muted-foreground" />
          </div>
        )}
        <div className="absolute top-2.5 left-2.5" onClick={e => e.stopPropagation()}>
          <Checkbox checked={isSel} onCheckedChange={() => toggleSelect(item.id)} className="bg-white/90 shadow-sm" />
        </div>
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" onClick={e => e.stopPropagation()}>
          <Button
            size="sm"
            variant="secondary"
            className="rounded-xl"
            onClick={() => { uploadItemRef.current = item; fileInputRef.current?.click(); }}
          >
            <Upload size={13} className="mr-1" />{item.image_url ? 'Change' : 'Upload'}
          </Button>
        </div>
        <div className="absolute top-2.5 right-2.5 flex gap-1 flex-col items-end">
          {item.tags?.includes('popular') && (
            <span className="bg-warning text-white text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-0.5">
              <Star size={10} />Popular
            </span>
          )}
          {!item.is_available && (
            <span className="bg-error text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">Unavailable</span>
          )}
          {item.schedule?.enabled && (
            <span className="bg-info text-white text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-0.5">
              <Clock size={10} />Scheduled
            </span>
          )}
        </div>
      </div>
      <div className="p-3.5">
        <div className="flex justify-between items-start mb-1.5">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-[13px] text-foreground dark:text-white truncate">{item.name}</h3>
            {item.name_ar && <p className="text-[11px] text-muted-foreground truncate" dir="rtl">{item.name_ar}</p>}
          </div>
          <span className="text-sm font-bold text-primary ml-2 whitespace-nowrap">{cur()} {item.price}</span>
        </div>
        <div className="flex justify-end mb-1.5"><MenuMarginBadge price={item.price} cost={recipeCost} /></div>
        {getModifierCount(item) > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {getModifierLabels(item).map((m, mi) => (
              <span key={mi} className="text-[9px] px-1.5 py-0.5 bg-muted dark:bg-stone-700 text-muted-foreground rounded-full">
                {m.name}: {m.count}
              </span>
            ))}
          </div>
        )}
        <div className="flex flex-wrap gap-1 mt-2">
          {(!item.branch_ids || item.branch_ids.length === 0) ? (
            <span className="text-[9px] px-1.5 py-0.5 bg-success/10 text-success rounded-full">All Branches</span>
          ) : (
            item.branch_ids.slice(0, 2).map(bid => (
              <span key={bid} className="text-[9px] px-1.5 py-0.5 bg-muted/50 dark:bg-stone-700 text-muted-foreground rounded-full flex items-center gap-0.5">
                <Building2 size={8} />{getBranchName(bid)}
              </span>
            ))
          )}
          {(item.branch_ids?.length || 0) > 2 && (
            <span className="text-[9px] px-1.5 py-0.5 bg-muted/50 text-muted-foreground rounded-full">
              +{item.branch_ids.length - 2}
            </span>
          )}
        </div>
        {item.platform_ids?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {item.platform_ids.slice(0, 3).map(pid => (
              <span key={pid} className="text-[9px] px-1.5 py-0.5 bg-info/10 text-info rounded-full flex items-center gap-0.5">
                <Globe size={8} />{getPlatformName(pid)}
              </span>
            ))}
            {item.platform_ids.length > 3 && (
              <span className="text-[9px] px-1.5 py-0.5 bg-info/10 text-info rounded-full">
                +{item.platform_ids.length - 3}
              </span>
            )}
          </div>
        )}
        <div className="flex justify-end gap-1 mt-2.5 pt-2 border-t border-border dark:border-stone-700">
          <Button size="icon" variant="ghost" className="h-7 w-7 rounded-lg text-muted-foreground hover:text-primary" onClick={() => handleEdit(item)} data-testid={`edit-${item.id}`}>
            <Edit size={13} />
          </Button>
          <Button size="icon" variant="ghost" className="h-7 w-7 rounded-lg text-muted-foreground hover:text-error" onClick={() => handleDelete(item)} data-testid={`delete-${item.id}`}>
            <Trash2 size={13} />
          </Button>
        </div>
      </div>
    </div>
  );
}
